import sendQuery from './send-query';
import productEdges from './product-edges';

// Convert a plain object of Storefront ProductFilter values into GraphQL input literal syntax
// e.g. { variantMetafield: { namespace: 'astr', key: 'plus_size', value: 'true' } } -> {variantMetafield:{namespace:"astr",key:"plus_size",value:"true"}}
const toGraphQLInput = (obj) => JSON.stringify(obj).replace(/"([^"]+)":/g, '$1:');

/**
 * Load products by collection ID or handle using Shopify Storefront API
 *
 * @param {Number} id Collection ID
 * @param {String} handle Collection handle
 * @param {Array} filters Storefront API ProductFilter objects (e.g. [{ variantMetafield: { namespace, key, value } }])
 * @param {Array} excludeTags Tags (case-insensitive) to exclude client-side, since Storefront filters can't negate a tag match
 */
export default async (id = 0, handle = '', filters = [], excludeTags = []) => {
  try {
    if (id === 0 && handle === '') {
      return [];
    }

    const filters_query = (filters.length > 0) ? `, filters: [${filters.map(toGraphQLInput).join(', ')}]` : '';

    let query_type = ``;
    if (id !== 0) {
      // query_type = `$collectionId: ID!`;
      query_type = `id: "gid://shopify/Collection/${id}"`;
    } else if (handle !== '') {
      // query_type = `$handle: String!`;
      query_type = `handle: "${handle}"`;
    }

    const sort_key = 'MANUAL'; // window.theme.collections.sorting.toUpperCase()

    let before_cursor = '';
    let after_cursor = '';
    if (window.theme.collections.page > 1) {
      // Jump to a specific page (e.g. page in url)
      if (window.theme.collections.startCursor === '' && window.theme.collections.endCursor === '') {
        let jump_cursor = '';

        for (let i = 1; i < window.theme.collections.page - 1; i++) {
          const jump_cursor_query = (i === 1) ? '' : `, after: "${jump_cursor}"`;

          const query = `
            query GetCollectionProducts @inContext(country: ${window.theme.country_code}, language: ${window.theme.language_code}) {
              collection(${query_type}) {
                id
                title
                products(first: ${Number(window.theme.collections.products_per_page) || 40}${jump_cursor_query}, sortKey: ${sort_key}${filters_query}) {
                  pageInfo {
                    hasNextPage
                    endCursor
                  }
                }
              }
            }
          `;
          // console.log(`Jump query (${i})`, query);

          const response = await sendQuery(query);

          if (!response.ok) {
            // throw new Error(`HTTP error ${response.status}`);
            console.log(`HTTP error ${response.status}`);
            return [];
          }

          const response_json = await response.json();
          console.log(`Jump response_json (${i})`, response_json);

          if (typeof response_json.data.collection.products.pageInfo !== 'undefined') {
            jump_cursor = response_json.data.collection.products.pageInfo.endCursor;
          }

          // Save cursor to correctly load the correct page after
          if (i === window.theme.collections.page - 2) {
            window.theme.collections.startCursor = response_json.data.collection.products.pageInfo.startCursor;
            window.theme.collections.endCursor = response_json.data.collection.products.pageInfo.endCursor;
          }
        }
      }
    }

    let per_page_query = `first: ${Number(window.theme.collections.products_per_page) || 40}`;

    if (window.theme.collections.startCursor !== '' && window.theme.collections.cursor_direction === 'before') {
      before_cursor = `, before: "${window.theme.collections.startCursor}"`;
      per_page_query = `last: ${Number(window.theme.collections.products_per_page) || 40}`;
    }

    if (window.theme.collections.endCursor !== '' && window.theme.collections.cursor_direction === 'after') {
      after_cursor = `, after: "${window.theme.collections.endCursor}"`;
      per_page_query = `first: ${Number(window.theme.collections.products_per_page) || 40}`;
    }

    const query = `
      query GetCollectionProducts @inContext(country: ${window.theme.country_code}, language: ${window.theme.language_code}) {
        collection(${query_type}) {
          id
          title
          products(${per_page_query}${before_cursor}${after_cursor}, sortKey: ${sort_key}${filters_query}) {
            pageInfo {
              hasNextPage
              hasPreviousPage
              endCursor
              startCursor
            }
            ${productEdges({get_description: false, get_collections: false, get_tags: true, get_metafields: true, get_inventory: true, get_metafields_for_tiles: true})}
          }
        }
      }
    `;

    // console.log('query', query);

    const response = await sendQuery(query);

    if (!response.ok) {
      // throw new Error(`HTTP error ${response.status}`);
      console.log(`HTTP error ${response.status}`);
      return [];
    }

    const response_json = await response.json();
    console.log('response_json', response_json);

    if (typeof response_json.errors !== 'undefined') {
      console.log('[Storefront API] Error in response:', response_json.errors);
      return [];
    }

    if (typeof response_json.data === 'undefined' || typeof response_json.data.collection === 'undefined' || typeof response_json.data.collection.products === 'undefined' || typeof response_json.data.collection.products.edges === 'undefined') {
      console.log('[Storefront API] No data', response_json);
      return [];
    }

    if (typeof response_json.data.collection.products.pageInfo !== 'undefined') {
      window.theme.collections.startCursor = response_json.data.collection.products.pageInfo.startCursor;
      window.theme.collections.endCursor = response_json.data.collection.products.pageInfo.endCursor;
    }

    const edges = response_json.data.collection.products.edges;
    const filteredEdges = edges.filter(edge => {
      if (!edge.node.availableForSale) return false;

      // Exclude parent from combined listings (combined_product is only set to 'true' on those parents)
      if (edge.node.astrCombinedProduct?.value === 'true') return false;

      if (excludeTags.length && edge.node.tags?.some(tag => excludeTags.includes(tag.toLowerCase()))) return false;

      return true;
    });

    return {
      ...response_json.data.collection,
      products: {
        ...response_json.data.collection.products,
        edges: filteredEdges
      }
    };

    // return response_json.data.collection;
  } catch (error) {
    console.log('[Storefront API]', error);
    return [];
  }
};
